import React, { useState } from 'react'
import cn from 'classnames'
import { Capitalize } from 'js/utils'

type TabProps = {
  count: any
  type: string
  selected?: boolean
  onChangeTab?: (event: React.SyntheticEvent) => void
  children: React.ReactNode
}

export function Tab({ count, type, selected, onChangeTab }: TabProps) {
  return (
    <li key={type}>
      <button
        aria-controls={type}
        onClick={onChangeTab}
        className={cn(
          'relative w-full rounded-t-8 lg:rounded-t-12',
          'px-12 pt-12',
          'type-upper-fat',
          'border-l-1 border-r-1 border-t-1 border-Border',
          'group bg-Bg',
          selected ? 'pb-12' : 'cursor-pointer border-b-1 pb-11',
        )}
        style={{ top: 1 }}
      >
        {Capitalize(type)}
        <span className="sr-only">{' ('}</span>
        <span
          className={cn(
            'h-24 px-12',
            'inline-flex',
            'rounded-full',
            'ml-8 transition',
            selected
              ? 'bg-grey-lightest bg-opacity-40 text-Type'
              : 'bg-green-lightest text-Brand group-hover:bg-Brand group-hover:text-TypeConstant',
          )}
        >
          <span className="m-auto pt-1">{count}</span>
        </span>
        <span className="sr-only">)</span>
      </button>
    </li>
  )
}

type TabsHeaderProps = {
  children: React.ReactNode
}

function TabsHeader({ children }: TabsHeaderProps) {
  if (React.Children.count(children) <= 1) return null
  return (
    <div className="mb-40 mt-48 border-b-1 border-Border text-center lg:mb-64 lg:mt-64">
      <ul className="mx-16 grid max-w-576 grid-cols-2 gap-2 md:mx-auto md:gap-4">
        {children}
      </ul>
    </div>
  )
}

type Props = {
  children: React.ReactElement[]
}

export function Tabs({ children }: Props) {
  const [selectedTab, setSelectedTab] = useState(() => {
    return React.Children.toArray(children).findIndex((child) => {
      if (!React.isValidElement(child)) {
        throw new Error('typeguard: Tabs children is not an element')
      }
      return child.props.count > 0
    })
  })

  const changeTab = (e, index) => {
    e.preventDefault()
    setSelectedTab(index)
  }

  return (
    <div>
      <TabsHeader>
        {React.Children.map(children, (child, index) => {
          if (!child.props.count) return null
          return (
            <Tab
              {...child.props}
              onChangeTab={(e) => changeTab(e, index)}
              selected={selectedTab === index}
            />
          )
        })}
      </TabsHeader>
      {React.Children.map(children, (child, index) => {
        return (
          <div id={child.props.type}>
            {index === selectedTab && children[selectedTab].props.children}
          </div>
        )
      })}
    </div>
  )
}
